import styles from './constStyles.module.scss';
import { PlanFeatureItem, PlanKind, PlanModelItem } from 'src/types';
import { IconName } from 'src/components/Icon';
import { ChatCircleText, Check, Images } from '@phosphor-icons/react';
import { TooltipWithModels } from 'src/pages/PaymentBetaPage/componentsV2/TooltipWithModels';

export const PLAN_KIND_TITLE_MAP_V3: Partial<
  Record<
    PlanKind,
    { fullTitle: string; shortTitle: string; description: string }
  >
> = {
  [PlanKind.MAX_05_MONTHLY]: {
    fullTitle: 'Standard',
    shortTitle: 'Standard',
    description: 'Perfect for individuals getting started with AI automation',
  },
  [PlanKind.MAX_05_ANNUALLY]: {
    fullTitle: 'Standard',
    shortTitle: 'Standard',
    description: 'Perfect for individuals getting started with AI automation',
  },
  [PlanKind.MAX_10_MONTHLY]: {
    fullTitle: 'Pro',
    shortTitle: 'Pro',
    description: 'For power users who need more capacity and advanced features',
  },
  [PlanKind.MAX_10_ANNUALLY]: {
    fullTitle: 'Pro',
    shortTitle: 'Pro',
    description: 'For power users who need more capacity and advanced features',
  },
  [PlanKind.MAX_20_MONTHLY]: {
    fullTitle: 'Ultra',
    shortTitle: 'Ultra',
    description: 'For professionals who demand the best AI capabilities',
  },
  [PlanKind.MAX_20_ANNUALLY]: {
    fullTitle: 'Ultra',
    shortTitle: 'Ultra',
    description: 'For professionals who demand the best AI capabilities',
  },
  [PlanKind.MAX_30_MONTHLY]: {
    fullTitle: 'Max',
    shortTitle: 'Max',
    description: 'Unlimited AI power for teams and heavy users',
  },
  [PlanKind.MAX_30_ANNUALLY]: {
    fullTitle: 'Max',
    shortTitle: 'Max',
    description: 'Unlimited AI power for teams and heavy users',
  },
  [PlanKind.BUSINESS_10_MONTHLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
    description: 'For power teams who need more capacity and advanced features',
  },
  [PlanKind.BUSINESS_10_ANNUALLY]: {
    fullTitle: 'Business Pro',
    shortTitle: 'Pro',
    description: 'For power teams who need more capacity and advanced features',
  },
  [PlanKind.BUSINESS_20_MONTHLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
    description: 'For businesses who demand the best AI capabilities',
  },
  [PlanKind.BUSINESS_20_ANNUALLY]: {
    fullTitle: 'Business Ultra',
    shortTitle: 'Ultra',
    description: 'For businesses who demand the best AI capabilities',
  },
  [PlanKind.BUSINESS_30_MONTHLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
    description: 'Unlimited AI power for teams and heavy users',
  },
  [PlanKind.BUSINESS_30_ANNUALLY]: {
    fullTitle: 'Business Max',
    shortTitle: 'Max',
    description: 'Unlimited AI power for teams and heavy users',
  },
};

export const MODEL_LIST_PREMIUM: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT-5.1',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude 4.6 Opus',
  },
  {
    iconName: 'chatGPTLogo',
    title: 'o3',
  },
  {
    iconName: 'xaiLogoJSX',
    title: 'Grok Code Fast',
  },
];

export const MODEL_LIST_PREMIUM_V3: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT-5 High Thinking',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude 4.6 Opus',
  },
  {
    iconName: 'chatGPTLogo',
    title: 'o3',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Pro (Preview)',
  },
  {
    iconName: 'xaiLogoJSX',
    title: 'Grok Code Fast',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude 4.6 Sonnet',
  },
];

export const MODEL_LIST_LIMITED_V3: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT-5.1',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude 4.6 Sonnet',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 3.0 Pro (Preview)',
  },
  {
    iconName: 'deepSeekLogo',
    title: 'DeepSeek R1',
  },
];

export const MODEL_LIST_LIMITED_STANDARD_V3: PlanModelItem[] = [
  {
    iconName: 'chatGPTLogo',
    title: 'GPT-5.1',
  },
  {
    iconName: 'claudeLogo',
    title: 'Claude 4.6 Sonnet',
  },
  {
    iconName: 'googleGeminiProLogo',
    title: 'Gemini 2.5 Pro',
  },
  {
    iconName: 'deepSeekLogo',
    title: 'DeepSeek R1',
  },
];

export const ALL_TEXT_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT-5.1, GPT-5, GPT-5 Mini, GPT-4.1, GPT-4.1 Mini',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude 4.6 Sonnet, Claude 4 Sonnet',
  },
  {
    icon: 'googleGeminiProLogo',
    models:
      'Gemini 3.0 Pro (Preview), Gemini Image Pro 3.0 (Preview), Gemini 2.5 Pro, Gemini 2.5 Flash',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek R1, DeepSeek V3',
  },
  {
    icon: 'xaiLogoJSX',
    models: 'Grok 4',
  },
  {
    icon: 'metaLogoJSX',
    models: 'Llama 4 Scout, Llama 4 Maverick',
  },
  {
    icon: 'alibabaLogoJSX',
    models: 'Qwen 3 32B (Cerebras Ultra Fast), Qwen 3 32B (Groq Fast)',
  },
  {
    icon: 'mistralLogo',
    models: 'Mistral Large, Mistral Small',
  },
  {
    icon: 'cohereLogo',
    models: 'Cohere Command R+, Cohere Command R',
  },
  {
    icon: 'nova',
    models: 'Amazon Nova Pro, Amazon Nova Lite, Amazon Nova Micro',
  },
];

export const ALL_TEXT_MODELS_STANDARD: { icon: IconName; models: string }[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT-5.1, GPT-5, GPT-5 Mini, GPT-4.1, GPT-4.1 Mini',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude 4.6 Sonnet, Claude 4 Sonnet',
  },
  {
    icon: 'googleGeminiProLogo',
    models: 'Gemini 2.5 Pro, Gemini 2.5 Flash',
  },
  {
    icon: 'deepSeekLogo',
    models: 'DeepSeek R1, DeepSeek V3',
  },
  {
    icon: 'xaiLogoJSX',
    models: 'Grok 4',
  },
  {
    icon: 'metaLogoJSX',
    models: 'Llama 4 Scout, Llama 4 Maverick',
  },
  {
    icon: 'alibabaLogoJSX',
    models: 'Qwen 3 32B (Cerebras Ultra Fast), Qwen 3 32B (Groq Fast)',
  },
  {
    icon: 'mistralLogo',
    models: 'Mistral Large, Mistral Small',
  },
  {
    icon: 'cohereLogo',
    models: 'Cohere Command R+, Cohere Command R',
  },
  {
    icon: 'nova',
    models: 'Amazon Nova Pro, Amazon Nova Lite, Amazon Nova Micro',
  },
];

export const ALL_ADDITIONAL_PREMIUM_MODELS: {
  icon: IconName;
  models: string;
}[] = [
  {
    icon: 'chatGPTLogo',
    models: 'GPT-5.1, o3',
  },
  {
    icon: 'claudeLogo',
    models: 'Claude 4.6 Opus',
  },
  {
    icon: 'xaiLogoJSX',
    models: 'Grok  Code Fast',
  },
];

export const ALL_IMAGE_MODELS: { icon: IconName; models: string }[] = [
  {
    icon: 'ninjaPixProLogo',
    models: 'FLUX Schnell, FLUX.1 Kontext Pro, \n FLUX 1.1 PRO',
  },
  {
    icon: 'chatGPTLogo',
    models: 'GPT Image 1, DALL·E 3',
  },
  {
    icon: 'googleGeminiProLogo',
    models: 'Imagen 4 Ultra, Imagen 4, Imagen 4 Fast',
  },
  {
    icon: 'nova',
    models: 'Amazon Nova Canvas',
  },
  {
    icon: 'bedrockStableLogo',
    models: 'Stable Diffusion Core',
  },
];

// --- Common

export const ADDITIONAL_COMMON_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>World’s fastest AI agent</span>
        <span className={styles.itemDescription}>Up to 1,500 tokens/sec</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Code with specialized models</span>
        <span className={styles.itemDescription}>
          VS Code + Cline + GitHub integration
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Create and edit documents</span>
        <span className={styles.itemDescription}>
          Word, Excel, PowerPoint, PDFs, Markdown with built-in editors
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Build & deploy websites</span>
        <span className={styles.itemDescription}>
          Full stack development & dashboards
        </span>
      </div>
    ),
  },
];

export const ADDITIONAL_COMMON_BUSINESS_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Opt out of data training</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Workspace management</span>
      </div>
    ),
  },
];

// --- Standard

export const STANDARD_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: ChatCircleText,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>100</b> daily chat tasks, unthrottled
        </span>
      </div>
    ),
  },
  {
    Icon: Images,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>50</b> daily image tasks, unthrottled
        </span>
      </div>
    ),
  },
];

export const ADDITIONAL_STANDARD_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={[...ALL_TEXT_MODELS_STANDARD, ...ALL_IMAGE_MODELS]}
        id="Standard_Plan_Text_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>
            <b>35+ AI text & image models</b>
          </span>
        }
      />
    ),
    modelList: [
      ...MODEL_LIST_LIMITED_STANDARD_V3,
      ...([
        {
          iconName: 'ninjaOutlined',
          title: 'Flux 1.1 Pro',
        },
        {
          iconName: 'googleGeminiProLogo',
          title: 'Imagen 4 Ultra',
        },
        {
          iconName: 'chatGPTLogo',
          title: 'GPT Image 1',
        },
      ] as unknown as PlanModelItem[]),
    ],
  },
  ...ADDITIONAL_COMMON_FEATURES_V3,
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>1M</b> token context window
        </span>
      </div>
    ),
  },
];

// --- Pro

export const PRO_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: ChatCircleText,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>200</b> daily chat tasks, unthrottled
        </span>
      </div>
    ),
  },
  {
    Icon: Images,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>75</b> daily image tasks, unthrottled
        </span>
      </div>
    ),
  },
];

export const ADDITIONAL_PRO_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={[...ALL_TEXT_MODELS, ...ALL_IMAGE_MODELS]}
        id="Pro_Plan_Text_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>
            <b>35+ AI text & image models</b>
          </span>
        }
      />
    ),
    modelList: [
      ...MODEL_LIST_LIMITED_V3,
      ...([
        {
          iconName: 'ninjaOutlined',
          title: 'Flux 1.1 Pro',
        },
        {
          iconName: 'googleGeminiProLogo',
          title: 'Imagen 4 Ultra',
        },
        {
          iconName: 'chatGPTLogo',
          title: 'GPT Image 1',
        },
      ] as unknown as PlanModelItem[]),
    ],
  },
  ...ADDITIONAL_COMMON_FEATURES_V3,
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>2M</b> token context window
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>2</b> concurrent virtual machines
        </span>
        <span className={styles.itemDescription}>
          Run multiple tasks simultaneously
        </span>
      </div>
    ),
  },
];

// --- Ultra

export const ULTRA_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: ChatCircleText,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>400</b> daily chat tasks, unthrottled
        </span>
      </div>
    ),
  },
  {
    Icon: Images,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>150</b> daily image tasks, unthrottled
        </span>
      </div>
    ),
  },
];

export const ADDITIONAL_ULTRA_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={[
          ...ALL_ADDITIONAL_PREMIUM_MODELS,
          ...ALL_TEXT_MODELS,
          ...ALL_IMAGE_MODELS,
        ]}
        id="Ultra_Plan_Premium_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>
            <b>40+ AI text & image models</b>
          </span>
        }
      />
    ),
    modelList: MODEL_LIST_PREMIUM_V3,
    hideAndMoreModels: false,
  },
  ...ADDITIONAL_COMMON_FEATURES_V3,
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>3M</b> token context window
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>5</b> concurrent virtual machines
        </span>
        <span className={styles.itemDescription}>
          Run multiple tasks simultaneously
        </span>
      </div>
    ),
  },
];

// --- Max

export const MAX_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: ChatCircleText,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>1,000</b> daily chat tasks, unthrottled
        </span>
      </div>
    ),
  },
  {
    Icon: Images,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemDescription}>
          <b>250</b> daily image tasks, unthrottled
        </span>
      </div>
    ),
  },
];

export const ADDITIONAL_MAX_FEATURES_V3: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={[
          ...ALL_ADDITIONAL_PREMIUM_MODELS,
          ...ALL_TEXT_MODELS,
          ...ALL_IMAGE_MODELS,
        ]}
        id="Max_Plan_Text_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>
            <b>40+ AI text & image models</b>
          </span>
        }
      />
    ),
    modelList: MODEL_LIST_PREMIUM_V3,
  },
  ...ADDITIONAL_COMMON_FEATURES_V3,
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>Infinite</b> intelligent context
        </span>
        <span className={styles.itemDescription}>Maximum productivity</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          <b>10</b> concurrent virtual machines
        </span>
        <span className={styles.itemDescription}>
          Enterprise-grade performance
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>All MCP tools</span>
      </div>
    ),
  },
];

// --- Business

export const ADDITIONAL_BUSINESS_PRO_FEATURES: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>80 SuperNinja tasks per month</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>200 chat tasks per day</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          75 Image generation & editing tasks per day
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>World’s fastest AI agent</span>
        <span className={styles.itemDescription}>Up to 1,500 tokens/sec</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Code with specialized models</span>
        <span className={styles.itemDescription}>
          VS Code + Cline + GitHub integration
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Create & edit documents</span>
        <span className={styles.itemDescription}>
          Word, Excel, PowerPoint, PDFs, Markdown with built-in editors
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Build & deploy websites</span>
        <span className={styles.itemDescription}>
          Full stack development & dashboards
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_TEXT_MODELS}
        id="Business_Pro_Plan_Text_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>25+ AI text models</span>
        }
      />
    ),
    modelList: [...MODEL_LIST_LIMITED_V3],
  },
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_IMAGE_MODELS}
        id="Business_Pro_Plan_Image_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>10+ AI image models</span>
        }
      />
    ),
    modelList: [
      {
        iconName: 'ninjaOutlined',
        title: 'Flux 1.1 Pro',
      },
      {
        iconName: 'googleGeminiProLogo',
        title: 'Imagen 4 Ultra',
      },
      {
        iconName: 'chatGPTLogo',
        title: 'GPT Image 1',
      },
    ],
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>2 concurrent virtual machines</span>
        <span className={styles.itemDescription}>
          Run multiple tasks simultaneously
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>2M token context window</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Opt out of data training</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Workspace management</span>
      </div>
    ),
  },
];

export const ADDITIONAL_BUSINESS_ULTRA_FEATURES: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitleBold}>
          Everything in Business Pro, plus
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>160 SuperNinja tasks per month</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>400 chat tasks per day</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          150 image generation and editing tasks per day
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <TooltipWithModels
        models={ALL_ADDITIONAL_PREMIUM_MODELS}
        id="Business_Ultra_Plan_Premium_Tooltip"
        hoveredText={
          <span className={styles.dottedUnderline}>
            Additional premium AI models
          </span>
        }
      />
    ),
    modelList: [...MODEL_LIST_PREMIUM],
    hideAndMoreModels: true,
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>5 concurrent virtual machines</span>
        <span className={styles.itemDescription}>
          Run multiple tasks simultaneously
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>3M token context window</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Opt out of data training</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Workspace management</span>
      </div>
    ),
  },
];

export const ADDITIONAL_BUSINESS_MAX_FEATURES: PlanFeatureItem[] = [
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitleBold}>
          Everything in Business Ultra, plus
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Unlimited SuperNinja tasks</span>
        <span className={styles.itemDescription}>No limits on standard</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>1,000 chat tasks per day</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>
          250 Image generation & editing per day
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>10 concurrent virtual machines</span>
        <span className={styles.itemDescription}>
          Enterprise-grade performance
        </span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Infinite intelligent context</span>
        <span className={styles.itemDescription}>Maximum productivity</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>All MCP tools</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Opt out of data training</span>
      </div>
    ),
  },
  {
    Icon: Check,
    title: (
      <div className={styles.itemWrapper}>
        <span className={styles.itemTitle}>Workspace management</span>
      </div>
    ),
  },
];
